import React, { useEffect, useState } from 'react';
import { Orders_text } from '../../../constant/Translation/OrdersText';
import { General_text } from '../../../constant/Translation/General';
import {
  file_icon,
  download_icon,
  delete_icon,
  loading_icon,
} from '../../Shared/Constant/Icons/AllIcons';
import { Image_upload } from '../../../constant/functions';
import axios from 'axios';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import { get_order_details } from '../../../store/feature/store_orderSlice';

const Invoice = ({
  order_details,
  setShowError,
  setError_message,
  setShowSuccess,
  setSuccess_message,
}) => {
  const router = useRouter();
  const dispatch = useDispatch();

  const [uploadedFile, setUploadedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setUploadedFile(
      order_details?.invoice_url
        ? {
            name: order_details?.invoice_url?.split('/').pop(),
            size: '',
            type: '',
            file: order_details?.invoice_url,
            url: order_details?.invoice_url,
          }
        : null
    );
  }, [order_details]);

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'];
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

      if (!allowedTypes.includes(fileExtension)) {
        setShowError(true);
        setError_message(Orders_text.orderdetail.please_select_valid_file_type);
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        setShowError(true);
        setError_message(
          Orders_text.orderdetail.file_size_must_be_less_than_5mb
        );
        return;
      }

      setUploadedFile({
        name: file.name,
        size: formatFileSize(file.size),
        type: file.type,
        file: file,
        url: URL.createObjectURL(file),
      });
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Handle file download
  const handleDownload = () => {
    if (uploadedFile && uploadedFile?.url) {
      //  make a new tab and open the url and download the file
      window.open(uploadedFile.url, '_blank');
      return;
    }
  };

  // Handle file removal
  const handleRemove = () => {
    setUploadedFile(null);
    // Reset file input
    const fileInput = document.getElementById('invoice-file-input');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Handle save
  const handleSave = async () => {
    if (!uploadedFile) {
      setShowError(true);
      setError_message(Orders_text.orderdetail.please_upload_a_file_first);
      return;
    }

    setIsSaving(true);
    try {
      const image_uri = await Image_upload(
        [{ path: null, full_file: uploadedFile.file }],
        setShowError,
        setError_message
      );
      // Here you would typically upload the file to your server
      // For now, we'll simulate the upload process
      await axios
        .post('/api/orders/update_order', {
          id: order_details.id,
          data: {
            operation: 'update_invoice_data',
            order: {
              invoice_url: image_uri[0],
              invoice_date: new Date().toISOString(),
            },
          },
        })
        .then((res) => {
          dispatch(
            get_order_details({
              id: order_details.id,
              bodyParam: { account_id: router.query.account_id },
            })
          );
        })
        .catch((error) => {
          console.log(error);
          setShowError(true);
          setError_message(error?.response?.data?.message);
          setIsSaving(false);
          setShowSuccess(false);
          return;
        });

      setShowSuccess(true);
      setSuccess_message(Orders_text.orderdetail.invoice_uploaded_successfully);

      // Reset the uploaded file state after successful save
      setUploadedFile(null);
      const fileInput = document.getElementById('invoice-file-input');
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (error) {
      console.log(error);
      setShowError(true);
      setError_message(
        Orders_text.orderdetail.failed_to_upload_invoice_please_try_again
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Trigger file input click
  const triggerFileInput = () => {
    document.getElementById('invoice-file-input').click();
  };

  return (
    <div className="w-full bg-white shadow-c-sm rounded-button p-4 sm:p-7 border-opacity-40 mt-5">
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-lg text-black font-semibold">
          {Orders_text.orderdetail.order_invoices}
        </h3>
      </div>

      {/* File Upload Area */}
      {!uploadedFile && (
        <div className="border-2 border-dashed border-primary rounded-button  text-center p-2 ">
          <div className="flex flex-col items-center  space-y-2">
            <div className="text-primary">{file_icon}</div>
            <div>
              <p className="text-lg font-semibold text-gray-700 mb-2">
                {Orders_text.orderdetail.upload_invoice}
              </p>
              <p className="text-sm text-gray-500">
                {Orders_text.orderdetail.supported_formats}
              </p>
            </div>
            <button
              onClick={triggerFileInput}
              className="btn-primary px-6 py-2 rounded-button font-medium"
              disabled={isUploading}
            >
              {isUploading
                ? Orders_text.orderdetail.uploading
                : Orders_text.orderdetail.choose_file}
            </button>
          </div>
          <input
            id="invoice-file-input"
            type="file"
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            onChange={handleFileUpload}
          />
        </div>
      )}

      {/* File Display */}
      {uploadedFile && (
        <div className="space-y-4">
          <div className="border border-gray-200 rounded-button p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="text-primary">{file_icon}</div>
                <div>
                  <p className="font-medium text-gray-900">
                    {uploadedFile.name}
                  </p>
                  <p className="text-sm text-gray-500">{uploadedFile.size}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleDownload}
                  className="p-2 text-primary hover:text-success transition-colors"
                  title="Download"
                >
                  {download_icon}
                </button>
                <button
                  onClick={handleRemove}
                  className="p-2 text-primary hover:text-error transition-colors"
                  title="Remove"
                >
                  {delete_icon}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload New File Button (when file is already uploaded) */}
      {uploadedFile && (
        <div className="flex justify-between items-center mt-5 border-t border-gray-200 pt-5">
          {uploadedFile && (
            <div className=" ">
              <button
                onClick={triggerFileInput}
                className="btn-primary-outline px-4 py-2 rounded-button font-medium"
                disabled={isUploading}
              >
                {Orders_text.orderdetail.upload_new_file}
              </button>
              <input
                id="invoice-file-input"
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={handleFileUpload}
              />
            </div>
          )}

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="btn-primary-fill px-6 py-2 rounded-button font-medium flex items-center gap-2"
            >
              {General_text.general.save}
              {isSaving && loading_icon}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoice;
